<div class="chat-shell">
  <div class="chat-header">
    <div>
      <div class="title">SME lesson workspace</div>
      <div class="subtitle">
        <span *ngIf="selectedLessonId">Lesson #{{ selectedLessonId }} · {{ lifecycleLabel(selectedLessonLifecycleStatus) }}</span>
        <span *ngIf="!selectedLessonId">No lesson open — use chat to choose</span>
      </div>
    </div>

    <div class="actions">
      <button
        type="button"
        class="btn btn-outline-success btn-compact"
        *ngIf="selectedLessonId && selectedLessonIsEmpty() && !toc"
        [disabled]="!selectedLessonId || pptGenBusy || lessonAiBusy || tocBusy || slideBusy"
        (click)="generateTocFromChat()"
      >
        Generate TOC
      </button>
      <button
        type="button"
        class="btn btn-outline-success btn-compact"
        [disabled]="!selectedLessonId || pptGenBusy || lessonAiBusy || tocBusy || slideBusy"
        (click)="generatePptFromChat()"
      >
        {{ pptGenBusy ? 'Generating…' : 'Generate PPT' }}
      </button>
      <button class="btn btn-outline-secondary btn-compact" (click)="goPowerUserMode()">
        Power user mode
      </button>
    </div>
  </div>

  <div class="chat-body two-col">
    <!-- Left: chat only -->
    <div class="col-chat">
      <div class="panel messages-panel">
        <div class="panel-title">Chat</div>

        <div class="messages">
          <div class="message" *ngFor="let m of messages" [class.user]="m.role==='user'" [class.assistant]="m.role==='assistant'" [class.system]="m.role==='system'">
            <div class="role">{{ m.role }}</div>
            <div class="text">{{ m.text }}</div>
          </div>
        </div>

        <div class="chat-toolbar chat-toolbar-compact">
          <button type="button" class="btn btn-outline-primary btn-compact" (click)="toggleLessonPicker()">
            {{ showLessonPicker ? 'Hide lesson list' : 'All lessons' }}
          </button>
          <button type="button" class="btn btn-outline-secondary btn-compact" (click)="refreshLessonsFromChat()" [disabled]="loadingLessons">
            Refresh lessons
          </button>
          <button type="button" class="btn btn-outline-success btn-compact" (click)="startAiLessonChat()" [disabled]="lessonAiBusy || tocBusy || slideBusy">
            AI new lesson
          </button>
          <button type="button" class="btn btn-success btn-compact" (click)="goCreateLesson()">Create (form)</button>
        </div>

        <div class="lesson-picker panel-inset" *ngIf="showLessonPicker">
          <div class="picker-title">Choose a lesson</div>
          <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
            <label class="small text-muted mb-0">Show workflow</label>
            <select
              class="form-select form-select-sm w-auto"
              [(ngModel)]="lessonLifecycleFilter"
              (ngModelChange)="onLessonLifecycleFilterChange()"
            >
              <option value="">All</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="RELEASED">Released</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
          <div *ngIf="loadingLessons" class="muted small">Loading…</div>
          <div *ngIf="!loadingLessons && !lessons?.length" class="muted small">No lessons yet.</div>
          <div class="lesson-list-compact" *ngIf="lessons?.length">
            <button
              type="button"
              class="lesson-item"
              *ngFor="let l of lessons"
              (click)="pickLesson(l.lessonId)"
              [class.active]="l.lessonId === selectedLessonId"
            >
              <div class="lesson-title">{{ l.lessonTitle }}</div>
              <div class="lesson-meta">
                #{{ l.lessonId }} · v{{ l.lessonVersion }} · {{ l.lessonStatus }} · {{ lifecycleLabel(l.lessonLifecycleStatus) }}
              </div>
            </button>
          </div>
        </div>

        <div class="create-inline" *ngIf="mode === 'createLesson'">
          <div class="panel-title small-title">Quick create</div>
          <div class="alert alert-warning py-2 small" *ngIf="licenseStatus && !canCreateLesson()">
            {{ licenseBlockMessage() }}
          </div>
          <div class="alert alert-info py-2 small" *ngIf="licenseStatus?.ok && licenseStatus?.available != null">
            Licenses available: {{ licenseStatus?.available }} of {{ licenseStatus?.total }}.
          </div>
          <app-sme-chat-lesson-create (create)="createLessonQuick($event)" (cancel)="onCancelCreateForm()"></app-sme-chat-lesson-create>
        </div>

        <div class="input">
          <input
            class="form-control"
            [(ngModel)]="inputText"
            (keyup.enter)="send()"
            [disabled]="lessonAiBusy || tocBusy || slideBusy"
            placeholder="Reply in chat, or e.g. Create a new lesson … / open lesson 2 / help"
          />
          <button class="btn btn-primary btn-compact" (click)="send()" [disabled]="lessonAiBusy || tocBusy || slideBusy">
            {{ lessonAiBusy || tocBusy || slideBusy ? '…' : 'Send' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Right: lesson details (TOC + slide) -->
    <div class="col-details details-doc" #colDetails>
      <div class="remote-busy remote-busy-sticky" *ngIf="remoteWorkBusy" role="status" aria-live="polite">
        <div class="remote-busy-main">
          <span class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
          <span class="remote-busy-text">{{ generationStatusPrimary() }}</span>
        </div>
        <div class="remote-busy-detail" *ngIf="generationStatusDetail()">{{ generationStatusDetail() }}</div>
      </div>

      <div class="panel details-banner" *ngIf="selectedLessonId">
        <div class="details-banner-title">Lesson details</div>
        <div class="muted small mb-2">TOC and slide editing — save from here when you make changes.</div>
        <div class="d-flex flex-wrap align-items-center gap-2">
          <label class="small mb-0 text-muted">Workflow</label>
          <select
            class="form-select form-select-sm w-auto"
            [ngModel]="selectedLessonLifecycleStatus"
            (ngModelChange)="onOpenLessonLifecycleChange($event)"
            [ngModelOptions]="{ standalone: true }"
          >
            <option value="IN_PROGRESS">In progress</option>
            <option value="RELEASED">Released</option>
            <option value="ARCHIVED">Archived</option>
          </select>
          <button
            type="button"
            class="btn btn-outline-warning btn-sm"
            *ngIf="canClearSelectedLesson()"
            (click)="clearLessonContentFromChat()"
            [disabled]="tocBusy || slideBusy"
          >
            Clear and Recreate TOC
          </button>
          <button
            type="button"
            class="btn btn-outline-danger btn-sm"
            *ngIf="selectedLessonId && !selectedLessonLinkedToProject()"
            (click)="deleteLessonFromChat()"
            [disabled]="tocBusy || slideBusy"
          >
            Delete lesson
          </button>
        </div>
      </div>

      <div class="panel panel-ref" *ngIf="selectedLessonId">
        <div class="panel-title">Reference files</div>
        <p class="muted small mb-2">
          Upload PDF or image files used when generating TOC and slides. For document-specific lessons, set
          <strong>Primary Reference</strong> to <strong>Yes</strong> on the source PDF.
        </p>
        <div *ngIf="referencesLocked()" class="alert alert-secondary py-2 small mb-2">
          Reference files are locked after slides are generated. Use Clear and Recreate TOC to change files.
        </div>
        <div *ngIf="hasExpiredOpenAiReferences()" class="alert alert-warning py-2 small mb-2">
          OpenAI upload expired for: {{ expiredOpenAiReferenceNames() }}.
          Refresh before generating TOC or slides (files stay on disk; only the OpenAI copy is renewed).
          <div class="mt-2">
            <button
              type="button"
              class="btn btn-sm btn-warning"
              (click)="refreshExpiredOpenAiUploads()"
              [disabled]="refRefreshOpenAiBusy || refUploading || tocBusy || slideBusy"
            >
              {{ refRefreshOpenAiBusy ? 'Refreshing…' : 'Refresh OpenAI upload' }}
            </button>
          </div>
        </div>
        <div *ngIf="references.length" class="table-responsive mb-2">
          <table class="table table-sm table-bordered mb-0">
            <thead class="table-light">
              <tr>
                <th>File</th>
                <th>Primary?</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let ref of references" [class.table-warning]="isReferenceOpenAiExpired(ref)">
                <td>
                  <span>{{ ref.label || ref.fileName }}</span>
                  <span class="text-muted small d-block">{{ formatRefSize(ref.fileSizeBytes) }}</span>
                  <span *ngIf="isReferenceOpenAiExpired(ref)" class="badge text-bg-warning">OpenAI expired</span>
                </td>
                <td>
                  <select
                    class="form-select form-select-sm"
                    [ngModel]="primaryReferenceYn(ref)"
                    (ngModelChange)="setPrimaryReference(ref, $event)"
                    [ngModelOptions]="{ standalone: true }"
                    [disabled]="referencesLocked() || refUploading"
                  >
                    <option value="N">No</option>
                    <option value="Y">Yes</option>
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    *ngIf="!referencesLocked()"
                    (click)="deleteReference(ref.refId)"
                    [disabled]="refUploading"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="muted small mb-2" *ngIf="!references.length">No reference files uploaded yet.</p>
        <div class="row g-2 align-items-end" *ngIf="!referencesLocked()">
          <div class="col-md-4">
            <label class="form-label small mb-1">Primary Reference?</label>
            <select
              class="form-select form-select-sm"
              [(ngModel)]="refUploadPrimary"
              [ngModelOptions]="{ standalone: true }"
              [disabled]="refUploading"
            >
              <option value="N">No</option>
              <option value="Y">Yes</option>
            </select>
          </div>
          <div class="col-md-8">
            <label class="form-label small mb-1">Add file(s)</label>
            <input
              type="file"
              class="form-control form-control-sm"
              accept=".pdf,image/png,image/jpeg,image/gif,image/webp"
              multiple
              [disabled]="refUploading || tocBusy || slideBusy"
              (change)="onReferenceFilesSelected($event)"
            />
            <div class="form-text">PDF or images, max {{ refMaxMbLabel }} each.</div>
          </div>
        </div>
        <div *ngIf="refUploadStatus" class="text-primary small mt-2" role="status">{{ refUploadStatus }}</div>
        <div *ngIf="refUploadError" class="text-danger small mt-2" style="white-space: pre-line">{{ refUploadError }}</div>
      </div>

      <div class="panel panel-doc" *ngIf="mode === 'toc' && toc">
        <div class="panel-title">
          Table of contents
          <span class="muted small">Edit locally, then save</span>
        </div>

        <div *ngIf="confirmDelete" class="confirm">
          <div class="confirm-text">
            Confirm delete {{ confirmDelete.kind }}?
          </div>
          <div class="confirm-actions">
            <button class="btn btn-danger btn-compact" (click)="confirmDeleteYes()">Delete</button>
            <button class="btn btn-outline-secondary btn-compact" (click)="confirmDeleteNo()">Cancel</button>
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label small text-muted mb-1">Lesson opening note</label>
          <textarea
            class="form-control-plaintext doc-input"
            rows="2"
            [(ngModel)]="toc.openingNote"
            (ngModelChange)="tocDirty = true"
            placeholder="Opening note for the lesson (from TOC AI)"
          ></textarea>
        </div>
        <div class="toc-actions toc-actions-compact">
          <button class="btn btn-outline-primary btn-compact" (click)="addSection()" [disabled]="aiGenerationBusy">Add section</button>
          <button class="btn btn-primary btn-compact" (click)="saveTOC()" [disabled]="aiGenerationBusy">Save TOC</button>
        </div>
        <p class="small text-muted mb-2" *ngIf="isDocumentStrictLesson(toc?.lessonContentMode)">
          <strong>Document-specific lesson</strong> — set <strong>PDF pages</strong> per slide (e.g. 12 or 12-14). Figures are cropped from the PDF file.
        </p>
        <div class="toc-tree toc-tree-doc">
          <div class="section section-doc" *ngFor="let s of toc.sections; let si = index">
            <div class="section-row section-row-compact">
              <input class="form-control-plaintext doc-input title" [(ngModel)]="s.title" (ngModelChange)="tocDirty = true" placeholder="Section title" />
              <button class="btn btn-outline-secondary btn-compact" (click)="moveSectionUp(si)">↑</button>
              <button class="btn btn-outline-secondary btn-compact" (click)="moveSectionDown(si)">↓</button>
              <button class="btn btn-outline-danger btn-compact" (click)="requestDeleteSection(si)">Delete</button>
              <button class="btn btn-outline-success btn-compact" (click)="addSlide(s)">+ Slide</button>
            </div>
            <textarea class="form-control-plaintext doc-input desc" [(ngModel)]="s.description" (ngModelChange)="tocDirty = true" rows="2" placeholder="Section description"></textarea>

            <div class="slides slides-doc">
              <div
                class="slide slide-doc"
                *ngFor="let sl of s.slides; let sli = index"
                [attr.id]="tocSlideDomId(sl.slideId)"
                [class.slide-doc-focused]="highlightedTocSlideId === sl.slideId"
                [class.slide-doc-generating]="isTocSlideGenerating(sl.slideId)"
              >
                <div class="slide-row slide-row-doc">
                  <input class="form-control-plaintext doc-input title" [(ngModel)]="sl.title" (ngModelChange)="tocDirty = true" placeholder="Slide title" />
                  <button class="btn btn-outline-secondary btn-compact" (click)="moveSlideUp(s, sli)">↑</button>
                  <button class="btn btn-outline-secondary btn-compact" (click)="moveSlideDown(s, sli)">↓</button>
                  <button class="btn btn-outline-danger btn-compact" (click)="requestDeleteSlide(si, sli)">Delete</button>
                  <button
                    class="btn btn-compact"
                    [class.btn-success]="!isTocSlideGenerating(sl.slideId)"
                    [class.btn-outline-primary]="isTocSlideGenerating(sl.slideId)"
                    (click)="openSlide(s, sl)"
                    [disabled]="tocBusy || slideBusy"
                  >
                    <ng-container *ngIf="isTocSlideGenerating(sl.slideId); else slideTocAction">
                      <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Generating…
                    </ng-container>
                    <ng-template #slideTocAction>{{ sl.status === 'FILLED' ? 'Open' : 'Generate' }}</ng-template>
                  </button>
                </div>
                <textarea class="form-control-plaintext doc-input desc" [(ngModel)]="sl.description" (ngModelChange)="tocDirty = true" rows="2" placeholder="Slide description"></textarea>
                <input
                  *ngIf="isDocumentStrictLesson(toc?.lessonContentMode) && !isQuizSlide(sl)"
                  class="form-control-plaintext doc-input desc mt-1"
                  type="text"
                  [(ngModel)]="sl.sourcePageRef"
                  (ngModelChange)="tocDirty = true"
                  placeholder="PDF pages (e.g. 12 or 12-14)"
                  title="1-based page number(s) in the uploaded PDF"
                  [class.is-invalid]="isDocumentStrictLesson(toc?.lessonContentMode) && !sl.sourcePageRef?.trim() && sl.status !== 'FILLED'"
                />
                <div *ngIf="isDocumentStrictLesson(toc?.lessonContentMode) && isQuizSlide(sl)" class="small text-muted mt-1">Quiz slide — no source pages</div>
              </div>
            </div>
          </div>
        </div>
        <div class="mb-3 mt-3">
          <label class="form-label small text-muted mb-1">Lesson closing note</label>
          <textarea
            class="form-control-plaintext doc-input"
            rows="2"
            [(ngModel)]="toc.closingNote"
            (ngModelChange)="tocDirty = true"
            placeholder="Closing note for the lesson (from TOC AI)"
          ></textarea>
        </div>
      </div>

      <div class="panel panel-slide-doc" *ngIf="mode === 'slide' && activeSlide && activeSlideEdit">
        <div class="panel-title panel-title-compact">
          Slide (edit + preview)
          <button class="btn btn-outline-secondary btn-compact" (click)="backToTOC()" [disabled]="slideBusy">Back to TOC</button>
        </div>

        <div class="panel preview panel-preview-doc">
          <div class="muted small">{{ slidePreviewNote }}</div>
          <div class="preview-actions">
            <button class="btn btn-outline-secondary btn-compact" (click)="previewExpanded = !previewExpanded">
              {{ previewExpanded ? 'Collapse preview' : 'Expand preview' }}
            </button>
          </div>
          <div class="slide-preview" [class.expanded]="previewExpanded">
            <div class="sp-title">{{ activeSlideEdit.slideTitle }}</div>
            <div class="sp-body" [class.with-image]="getPreviewImageUrl()">
              <div class="sp-image" *ngIf="getPreviewImageUrl() as prevImg">
                <img [attr.src]="prevImg" (error)="onPreviewLayoutImageError($event)" alt="slide preview image" loading="lazy" />
              </div>
              <div class="sp-text">
                <div class="sp-line" *ngFor="let ln of getPreviewTextLines()"
                     [style.marginLeft.px]="previewIndentPx(ln.indent)">
                  <span class="sp-bullet" *ngIf="ln.bullet">•</span>
                  <span class="sp-content">{{ ln.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="confirmDeleteElementIndex !== null" class="confirm">
          <div class="confirm-text">Confirm delete element?</div>
          <div class="confirm-actions">
            <button class="btn btn-danger btn-compact" (click)="confirmDeleteElementYes()">Delete</button>
            <button class="btn btn-outline-secondary btn-compact" (click)="confirmDeleteElementNo()">Cancel</button>
          </div>
        </div>

        <div class="slide-header slide-header-doc">
          <input class="form-control-plaintext doc-input slide-title-input" [(ngModel)]="activeSlideEdit.slideTitle" (ngModelChange)="slideDirty = true" placeholder="Slide title" />
          <div class="slide-header-actions slide-header-actions-row">
            <button class="btn btn-outline-secondary btn-compact" (click)="showSlideNotes = !showSlideNotes">
              {{ showSlideNotes ? 'Hide slide notes' : 'Slide notes' }}
            </button>
            <button class="btn btn-outline-secondary btn-compact" (click)="showElementNotes = !showElementNotes">
              {{ showElementNotes ? 'Hide el. notes' : 'Element notes' }}
            </button>
            <button class="btn btn-outline-secondary btn-compact" (click)="showAdvancedElementFields = !showAdvancedElementFields">
              {{ showAdvancedElementFields ? 'Hide adv.' : 'Advanced' }}
            </button>
            <button class="btn btn-outline-primary btn-compact" (click)="addSlideElement()">Add element</button>
            <button class="btn btn-primary btn-compact" (click)="saveSlideFromChat()">Save slide</button>
          </div>
        </div>

        <div class="notes notes-doc" *ngIf="showSlideNotes">
          <div class="note-block">
            <div class="label">Opening note</div>
            <textarea class="form-control-plaintext doc-input" [(ngModel)]="activeSlideEdit.openingNote" (ngModelChange)="slideDirty = true" rows="2"></textarea>
          </div>
          <div class="note-block">
            <div class="label">Closing note</div>
            <textarea class="form-control-plaintext doc-input" [(ngModel)]="activeSlideEdit.closingNote" (ngModelChange)="slideDirty = true" rows="2"></textarea>
          </div>
        </div>

        <div class="elements elements-doc">
          <div class="el el-doc" *ngFor="let el of activeSlideEdit.slideElements; let ei = index">
            <div class="el-head el-head-compact">
              <span class="badge bg-secondary badge-compact">{{ el.elementType }}</span>
              <span class="muted small el-id">id: {{ el.elementId }}</span>
              <button class="btn btn-outline-secondary btn-compact" (click)="toggleBullet(el)">
                {{ (el.bulletFlag || 'N').toUpperCase() === 'Y' ? 'Bullet: Y' : 'Bullet: N' }}
              </button>
              <div class="indent-controls">
                <button class="btn btn-outline-secondary btn-compact" (click)="indentDelta(el, -1)">−</button>
                <button class="btn btn-outline-secondary btn-compact" (click)="indentDelta(el, 1)">+</button>
              </div>
              <span class="spacer"></span>
              <button class="btn btn-outline-primary btn-compact" (click)="insertSlideElementAfter(ei)">Insert</button>
              <button class="btn btn-outline-secondary btn-compact" (click)="moveSlideElementUp(ei)">↑</button>
              <button class="btn btn-outline-secondary btn-compact" (click)="moveSlideElementDown(ei)">↓</button>
              <button class="btn btn-outline-danger btn-compact" (click)="deleteSlideElement(ei)">×</button>
            </div>

            <div class="row g-2 el-advanced" *ngIf="showAdvancedElementFields">
              <div class="col-md-6">
                <label class="label">Type</label>
                <select class="form-select form-select-sm form-select-compact" [(ngModel)]="el.elementType" (ngModelChange)="onSlideElementTypeChange(el)">
                  <option value="TEXT">TEXT</option>
                  <option value="IMAGE">IMAGE</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="label">Indent level</label>
                <input class="form-control-plaintext doc-input" type="number" [(ngModel)]="el.indentLevel" (ngModelChange)="slideDirty = true" />
              </div>
            </div>

            <div class="el-content-block">
              <label class="label">Content</label>
              <textarea class="form-control-plaintext doc-input compact-textarea"
                        [(ngModel)]="el.content"
                        (ngModelChange)="onSlideElementContentChange(el)"
                        rows="1"
                        (focus)="autoGrow1($event)"
                        (input)="autoGrow1($event)"></textarea>
            </div>

            <div class="el-image-block" *ngIf="normalizedElementType(el) === 'IMAGE'">
              <div class="el-img">
                <ng-container *ngIf="imageUrlForElement(el) as imgUrl; else missingImg">
                  <img
                    [attr.src]="imgUrl"
                    alt="slide image"
                    loading="lazy"
                    (load)="onImgLoad(el)"
                    (error)="onImgError(el)"
                  />
                  <div class="muted small mt-1 img-url" *ngIf="imageLoadState[el.elementId] === 'error'">
                    Status: failed to load in page (but may open in a new tab)
                  </div>
                </ng-container>
                <ng-template #missingImg>
                  <div class="img-missing">
                    <div class="img-missing-title">Image preview not available</div>
                    <div class="img-missing-sub">File: {{ el.content || '(empty)' }}</div>
                  </div>
                </ng-template>
              </div>
              <div class="d-flex flex-wrap gap-2 align-items-center mt-1">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-compact"
                  (click)="regenerateSlideImage(el)"
                  [disabled]="!!regeneratingImageElementId || remoteWorkBusy"
                >
                  {{ regeneratingImageElementId === el.elementId ? 'Regenerating…' : 'Regenerate image' }}
                </button>
                <span class="muted small" *ngIf="!resolveImagePromptForElement(el)">No prompt in notes / imagePrompt</span>
              </div>
              <label class="label mt-1">Image caption</label>
              <input class="form-control-plaintext doc-input" [(ngModel)]="el.imageCaption" (ngModelChange)="slideDirty = true" />
            </div>

            <div class="el-notes" *ngIf="showElementNotes">
              <div class="label">Notes</div>
              <textarea class="form-control-plaintext doc-input compact-textarea"
                        [(ngModel)]="el.notes"
                        (ngModelChange)="slideDirty = true"
                        rows="1"
                        (focus)="autoGrow1($event)"
                        (input)="autoGrow1($event)"></textarea>
            </div>
          </div>
        </div>

        <div class="toc-actions toc-actions-compact">
          <button class="btn btn-outline-primary btn-compact" (click)="addSlideElement()">Add element</button>
        </div>
      </div>

      <div class="panel placeholder-details" *ngIf="!selectedLessonId || (!toc && mode !== 'toc')">
        <div class="placeholder-title">Lesson details</div>
        <p class="muted mb-0" *ngIf="!selectedLessonId">
          Open a lesson from <strong>All lessons</strong> or follow the chat (e.g. <code>open lesson &lt;id&gt;</code>). Edits to the table of contents and slides will show here.
        </p>
        <p class="muted mb-2" *ngIf="selectedLessonId && !toc && tocBusy">
          Lesson #{{ selectedLessonId }} — generating table of contents…
        </p>
        <p class="muted mb-2" *ngIf="selectedLessonId && !toc && !tocBusy && selectedLessonIsEmpty()">
          Lesson #{{ selectedLessonId }} is empty. Generate a new table of contents from lesson properties.
        </p>
        <button
          type="button"
          class="btn btn-success btn-sm"
          *ngIf="selectedLessonId && !toc && !tocBusy && selectedLessonIsEmpty()"
          (click)="generateTocFromChat()"
        >
          Generate TOC
        </button>
      </div>
    </div>
  </div>
</div>
